// src/components/assistant/ChartRenderer.tsx
/**
 * ChartRenderer - Orchestrates chart rendering for the AI assistant.
 * 
 * This component:
 * 1. Takes a ChartSpec from the assistant
 * 2. Fetches data via useChartData (immediately, regardless of visibility)
 * 3. Only renders heavy ECharts components when visible (IntersectionObserver)
 * 
 * This improves performance by:
 * - Prefetching all chart data as specs arrive
 * - Deferring expensive chart renders until scrolled into view
 * - Showing lightweight placeholders for off-screen charts
 * 
 * Chart implementations are in @/components/charts/assistant/
 */

import React, { useMemo, useRef, useState, useEffect } from "react";
import { useChartData } from "@/hooks/assistant/useChartData";
import { useMetricCatalogMap } from "@/hooks/useMetricCatalogMap";
import type { ChartSpec } from "@/types/assistant";
import { getMetricSpec, getXMetricSpec, getYMetricSpec } from "@/types/assistant";
import { ChartErrorBoundary } from "./ErrorBoundary";

// Unified theme system
import { getCardAccent } from "@/utils/charts/theme";

// Core chart components (shared)
import { 
  ChartCard, 
  ChartError, 
  ChartMissing 
} from "@/components/charts/core";
import StatCard from "@/components/charts/base/StatCard";
import TreemapChart from "@/components/charts/base/TreemapChart";

// Assistant-specific chart components and helpers
import {
  AssistantBarChart,
  AssistantTimeSeriesChart,
  AssistantScatterChart,
  ASSISTANT_CHART_THEME,
  ASSISTANT_CHART_HEIGHT,
  deriveFormatOptions,
  getStackingMode,
  getChartStyle,
  deriveTrend,
} from "@/components/charts/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Viewport Detection Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to detect if an element is in the viewport.
 * Once visible, stays visible (no unmounting when scrolling away).
 */
function useInViewport(rootMargin = "100px"): [React.RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return; // Already visible, no need to observe

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing once visible
        }
      },
      { rootMargin } // Start loading slightly before entering viewport
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return [ref, isVisible];
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ChartRendererProps {
  spec: ChartSpec;
}

// ─────────────────────────────────────────────────────────────────────────────
// Theme Helpers
// ─────────────────────────────────────────────────────────────────────────────

const CHART_HEIGHT = ASSISTANT_CHART_HEIGHT;

/** Get card accent palette based on chart id */
function getChartPalette(chartId: string | undefined) {
  return getCardAccent(chartId ?? 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-export for external use
// ─────────────────────────────────────────────────────────────────────────────

export { ChartMissing };

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ChartRendererInner: React.FC<ChartRendererProps> = ({ spec }) => {
  // Viewport detection - charts only render when scrolled into view
  const [viewportRef, isInViewport] = useInViewport("200px");
  
  // ALWAYS fetch data, regardless of visibility (prefetch)
  const { data, isLoading, error } = useChartData(spec);
  const { catalogMap } = useMetricCatalogMap();
  const palette = getChartPalette(spec.id);

  // Extract metric specs for format derivation
  const metricSpec = getMetricSpec(spec);
  const xMetricSpec = getXMetricSpec(spec);
  const yMetricSpec = getYMetricSpec(spec);

  // Derive format options from metric spec and catalog
  const yFormatOptions = useMemo(() => 
    deriveFormatOptions(metricSpec, catalogMap, spec.y_axis),
    [metricSpec, catalogMap, spec.y_axis]
  );
  
  const xFormatOptions = useMemo(() => 
    deriveFormatOptions(xMetricSpec, catalogMap, spec.x_axis),
    [xMetricSpec, catalogMap, spec.x_axis]
  );
  
  const yScatterFormatOptions = useMemo(() => 
    deriveFormatOptions(yMetricSpec, catalogMap, spec.y_axis),
    [yMetricSpec, catalogMap, spec.y_axis]
  );

  // Handle stat_card specially (lightweight, always render)
  if (spec.type === "stat_card") {
    const statData = spec.stat_data ?? spec.stats?.[0];
    if (!statData) {
      return <ChartError title={spec.title} error="Missing stat_data for stat_card" />;
    }
    return (
      <StatCard
        label={statData.label}
        value={statData.value}
        format={statData.format}
        change={statData.change}
        changePeriod={statData.change_period}
        trend={statData.trend ?? deriveTrend(statData.change)}
        subtitle={statData.subtitle}
        accentColor={palette.accent}
        primaryColor={palette.primary}
      />
    );
  }

  // Show loading skeleton while data is fetching
  if (isLoading) {
    return (
      <div ref={viewportRef}>
        <ChartCard 
          title={spec.title} 
          subtitle={spec.description}
          isLoading
          height={CHART_HEIGHT}
        >
          <div />
        </ChartCard>
      </div>
    );
  }

  if (error) {
    return <ChartError title={spec.title} error={error instanceof Error ? error.message : "Failed to load chart"} />;
  }

  if (!data) {
    return <ChartError title={spec.title} error="No data available for this metric" />;
  }

  // Check for empty data based on type
  if ((data.type === "bar" || data.type === "distribution") && data.data.categories.length === 0) {
    return <ChartError title={spec.title} error="No data available for this metric" />;
  }
  if ((data.type === "historical" || data.type === "multi_historical" || data.type === "time_series" || data.type === "comparison") && data.data.series.length === 0) {
    return <ChartError title={spec.title} error="No data available for this metric" />;
  }
  if (data.type === "scatter" && data.data.points.length === 0) {
    return <ChartError title={spec.title} error="No data available for this metric" />;
  }
  if (data.type === "treemap" && data.data.nodes.length === 0) {
    return <ChartError title={spec.title} error="No data available for this metric" />;
  }

  // Determine if we should use color_by_value for bar charts
  const shouldColorByValue = spec.color_by_value ?? (metricSpec?.transform === "change");

  // If not in viewport yet, show a lightweight placeholder
  // Data is already loaded, so when scrolled into view it renders instantly
  if (!isInViewport) {
    return (
      <div ref={viewportRef}>
        <ChartCard 
          title={spec.title} 
          subtitle={spec.description}
          height={CHART_HEIGHT}
        >
          {/* Lightweight placeholder - data ready, waiting for scroll */}
          <div 
            className="flex items-center justify-center h-full text-white/30 text-sm"
            style={{ height: CHART_HEIGHT }}
          >
            Chart ready
          </div>
        </ChartCard>
      </div>
    );
  }

  // In viewport with data loaded - render the full chart
  return (
    <div ref={viewportRef}>
      <ChartCard 
        title={spec.title} 
        subtitle={spec.description}
        height={CHART_HEIGHT}
      >
        {/* Legacy historical types */}
        {(data.type === "historical" || data.type === "multi_historical") && (
          <AssistantTimeSeriesChart
            dates={data.data.dates}
            series={data.data.series}
            showLegend={spec.show_legend ?? data.data.series.length > 1}
            style={getChartStyle(spec)}
            stacking={getStackingMode(spec)}
            formatOptions={yFormatOptions}
            {...(spec.y_axis?.label ? { yAxisName: spec.y_axis.label } : {})}
            {...(spec.log_scale !== undefined ? { logScale: spec.log_scale } : {})}
          />
        )}

        {/* DSL v3: time_series */}
        {data.type === "time_series" && (
          <AssistantTimeSeriesChart
            dates={data.data.dates}
            series={data.data.series}
            showLegend={spec.show_legend ?? data.data.series.length > 1}
            style={getChartStyle(spec)}
            stacking={getStackingMode(spec)}
            formatOptions={yFormatOptions}
            {...(spec.y_axis?.label ? { yAxisName: spec.y_axis.label } : {})}
            {...(spec.log_scale !== undefined ? { logScale: spec.log_scale } : {})}
          />
        )}

        {/* DSL v3: comparison (indexed to 100) */}
        {data.type === "comparison" && (
          <AssistantTimeSeriesChart
            dates={data.data.dates}
            series={data.data.series}
            yAxisName="Indexed (100 = Start)"
            showLegend={spec.show_legend ?? data.data.series.length > 1}
            style="line"
          />
        )}

        {/* DSL v3: bar and distribution */}
        {(data.type === "bar" || data.type === "distribution") && (
          <AssistantBarChart
            categories={data.data.categories}
            values={data.data.values}
            colorByValue={shouldColorByValue}
            barColor={palette.primary}
            barHoverColor={palette.hover}
            formatOptions={yFormatOptions}
            {...(spec.y_axis?.label ? { yAxisName: spec.y_axis.label } : {})}
          />
        )}

        {/* DSL v3: scatter */}
        {data.type === "scatter" && (
          <AssistantScatterChart
            points={data.data.points}
            dotColor={palette.primary}
            xFormatOptions={xFormatOptions}
            yFormatOptions={yScatterFormatOptions}
            {...(spec.x_axis?.label ? { xAxisName: spec.x_axis.label } : {})}
            {...(spec.y_axis?.label ? { yAxisName: spec.y_axis.label } : {})}
          />
        )}

        {/* DSL v3: treemap */}
        {data.type === "treemap" && (
          <TreemapChart
            nodes={data.data.nodes}
            height={CHART_HEIGHT}
            showLabels={spec.show_labels ?? true}
            colorIsPercent
            sizeLabel={spec.metric_label ?? "Value"}
            colorLabel="Change"
            positiveColor={ASSISTANT_CHART_THEME.treemap.positive}
            negativeColor={ASSISTANT_CHART_THEME.treemap.negative}
          />
        )}
      </ChartCard>
    </div>
  );
};

// Memoize ChartRenderer - only re-render if spec ID changes
// (spec object might be new reference but same data during streaming)
const ChartRenderer = React.memo(ChartRendererInner, (prev, next) => {
  // If spec ID is the same, no need to re-render
  // The data fetching hook inside will handle updates
  return prev.spec.id === next.spec.id;
});

// Wrap with error boundary for production resilience
const ChartRendererWithErrorBoundary: React.FC<ChartRendererProps> = (props) => (
  <ChartErrorBoundary>
    <ChartRenderer {...props} />
  </ChartErrorBoundary>
);

export default ChartRendererWithErrorBoundary;
